import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IEmployeeUpn, IMasterData, IMasterDataObject } from 'models/models';
import { request } from 'services/apis';
import { toast } from 'react-toastify';

// Employee type for modal
export interface IEmployee {
  employeeId: string;
  employeeName: string;
  employeeUpn: string;
  isActive: boolean;
}

export interface IProjects {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  projectManagerId: string;
  projectManager: string;
  technicalLeadId: string;
  technicalLead: string;
}

export interface IMentors {
  id: string;
  staffMemberId: string;
  mentor01Id: string;
  mentor02Id: string;
  mentor03Id: string;
}

// Fetch all employees for modal
export const getEmployees = createAsyncThunk(
  'masterData/getEmployees',
  async () => {
    try {
      const response = await request.get<IEmployee[]>('/employees/id-name-upn');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);
export const createDesignation = createAsyncThunk(
  'masterData/createDesignation',
  async (payload: { name: string; description?: string }) => {
    try {
      const response = await request.post<IMasterDataObject>(
        '/Designation',
        payload
      );
      toast.success('Designation created successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to create designation');
      throw error;
    }
  }
);

export const getDesignations = createAsyncThunk(
  'masterData/getDesignations',
  async () => {
    try {
      const response = await request.get<IMasterDataObject[]>(
        '/Designation/designations'
      );
      return response.data;
    } catch (error) {
      toast.error('Failed to fetch designations');
      throw error;
    }
  }
);

export const updateDesignation = createAsyncThunk(
  'masterData/updateDesignation',
  async (payload: { id: string; name: string; description?: string }) => {
    try {
      const response = await request.put<IMasterDataObject>(
        `/Designation/${payload.id}`,
        payload
      );
      toast.success('Designation updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update designation');
      throw error;
    }
  }
);

export const getEmployeesUpns = createAsyncThunk(
  'masterData/getEmployeesUpns',
  async () => {
    try {
      const response = await request.get<IEmployeeUpn[]>(
        '/metadata/employees/name-upn'
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const getProjects = createAsyncThunk(
  'masterData/getProjects',
  async () => {
    try {
      const response = await request.get<IProjects[]>(
        '/Project/all?isActive=true'
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const createProject = createAsyncThunk<
  IProjects,
  {
    name: string;
    description: string;
    startDate: string;
    endDate: string | null;
    isActive: boolean;
  }
>(
  'masterData/createProject',
  async (payload: {
    name: string;
    description: string;
    startDate: string;
    endDate: string | null;
    isActive: boolean;
  }) => {
    try {
      const response = await request.post<IProjects>(
        '/Project/create',
        payload
      );
      toast.success('Project created successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to create project');
      throw error;
    }
  }
);

export const updateProject = createAsyncThunk<
  IProjects,
  {
    id: string;
    data: {
      name: string;
      description: string;
      startDate: string;
      endDate: string | null;
      isActive: boolean;
      projectManagerId?: string;
      technicalLeadId?: string;
    };
  }
>(
  'masterData/updateProject',
  async (payload: {
    id: string;
    data: {
      name: string;
      description: string;
      startDate: string;
      endDate: string | null;
      isActive: boolean;
      projectManagerId?: string;
      technicalLeadId?: string;
    };
  }) => {
    try {
      const response = await request.put<IProjects>(
        `/Project/${payload.id}`,
        payload.data
      );
      toast.success('Project updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update project');
      throw error;
    }
  }
);

// TO DO: Implement the getStaffMemberMentorByStaffMemberId function
export const getStaffMemberMentorByStaffMemberId = createAsyncThunk(
  'masterData/getStaffMemberMentorByStaffMemberId',
  async (staffMemberId: string) => {
    try {
      const response = await request.get<IMentors>(
        `/StaffMemberMentor/GetStaffMemberMentorByStaffMemberId/${staffMemberId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

const initialState: IMasterData & { employees: IEmployee[] } = {
  designations: [],
  employeeUpns: [],
  employees: [],
  projects: [],
  mentors: {
    id: '',
    staffMemberId: '',
    mentor01Id: '',
    mentor02Id: '',
    mentor03Id: ''
  }
};

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get designations
    builder.addCase(getDesignations.fulfilled, (state, { payload }) => {
      state.designations = payload;
    });

    // Create designation
    builder.addCase(createDesignation.fulfilled, (state, { payload }) => {
      state.designations.push(payload);
    });

    // Update designation
    builder.addCase(updateDesignation.fulfilled, (state, { payload }) => {
      const index = state.designations.findIndex((d) => d.id === payload.id);
      if (index !== -1) {
        state.designations[index] = payload;
      }
    });

    // Get employees for modal
    builder.addCase(getEmployees.fulfilled, (state, { payload }) => {
      state.employees = payload;
    });

    // Get projects
    builder.addCase(getProjects.fulfilled, (state, { payload }) => {
      state.projects = payload;
    });

    // Create project
    builder.addCase(createProject.fulfilled, (state, { payload }) => {
      state.projects.push(payload);
    });

    // Update project
    builder.addCase(updateProject.fulfilled, (state, { payload }) => {
      const index = state.projects.findIndex((p) => p.id === payload.id);
      if (index !== -1) {
        state.projects[index] = payload;
      }
    });

    // Get staff member mentor by staff member ID
    builder.addCase(
      getStaffMemberMentorByStaffMemberId.fulfilled,
      (state, { payload }) => {
        // Assuming payload is an array of projects or mentors
        state.mentors = payload; // Adjust based on actual response structure
      }
    );
    // Handle any errors or pending states if necessary
    builder.addCase(getEmployees.rejected, (state, action) => {
      console.error('Failed to fetch employees:', action.error.message);
    });
  }
});

const { reducer } = masterDataSlice;

export default reducer;
